import Edit from '@mui/icons-material/Edit';
import { Button, Grid, InputLabel, Paper, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Fragment, useMemo, useRef } from 'react';
import { useMutation } from 'react-query';
import { Link, RouteComponentProps } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { HashRoute } from '../../common/HashRoute';
import {
  capitalize,
  formatOnOffString,
} from '../../common/helperFunctions/formatter';
import { useStylesBase } from '../../common/styles';
import { isUrl } from '../../common/validators';
import { IBusinessUser, IUserAtom, Role, user } from '../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import {
  DevicesAndRoutingModal,
  getDeviceLabel,
  IBusinessDevices,
  IRingGroupSettings,
} from '../components/modals/DevicesAndRoutingModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { countryList, ICountryList } from '../subpages/countryList';

const useStyles = makeStyles((_theme) => ({
  secondsToRingDevicesInfo: {
    color: '#888',
  },
  minHeightGridItem: {
    minHeight: '81.5px',
  },
  endpointExists: {
    '& .MuiInputBase-input': {
      color: 'green',
    },
  },
  endpointDoesNotExists: {
    '& .MuiInputBase-input': {
      color: 'red',
    },
  },
}));

/* eslint-disable no-unused-vars */
enum EditModalHashes {
  InboundCallFlow = 'inbound-call-flow',
  ForwardDevices = 'forward-devices',
  KixieDevices = 'kixie-devices',
}
/* eslint-enable no-unused-vars */

const NOT_SET_LABEL = 'Not set';

interface IDevicesAndRoutingProps extends RouteComponentProps {
  id: string;
}

export function DevicesAndRouting({
  id,
  history,
  match,
}: IDevicesAndRoutingProps) {
  if (!id || !history || !match) return null;

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  // BEGIN DATA FETCHING CODE COPIED FROM ../../components/modals/DevicesAndRoutingModal
  //#region Ajax
  //============
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtom = userAtomLoadable.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));
  const { role } = useRecoilValueLoadable<IPermissions>(permissionsSelector)
    .contents as IPermissions;
  // Ring Group Settings
  // -------------------
  const ringGroupSettingsLoadable = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_RING_GROUP_SETTINGS,
      payloadJsonString: JSON.stringify({
        userid: id,
      }),
    })
  );
  const ringGroupSettingsCached = useRef<any>();
  ringGroupSettingsCached.current = useMemo(
    () =>
      ringGroupSettingsLoadable.state === 'loading'
        ? ringGroupSettingsCached.current
        : ringGroupSettingsLoadable.contents?.data?.result,
    [ringGroupSettingsLoadable]
  );

  // Business Devices
  // ----------------
  const data = {
    call: methodNameEnum.GET_BUSINESS_DEVICES,
  };
  const businessDevicesLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DEVICES,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const businessDevicesCached = useRef<any>([]);
  businessDevicesCached.current = useMemo(
    () =>
      businessDevicesLoadable.state === 'loading'
        ? businessDevicesCached.current
        : businessDevicesLoadable.contents?.response?.data?.result?.map(
            (device: any) => ({
              ...device,
              // stringified in order for equality comparisons to work
              isdefault: device.isdefault?.toString(),
              sipUserId: device.sipUserId?.toString(),
              userId: device.userId?.toString(),
            })
          ) ?? [],
    [businessDevicesLoadable]
  );

  const userDevicesLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_USER_DEVICES,
      payloadJsonString: JSON.stringify({
        call: 'getDevicesByUseridCheckEndpoint',
        userid: id,
      }),
    })
  );
  const setRefetchToggleForEndpoints = useSetRecoilState(
    refetchToggle(endpointEnum.GET_USER_DEVICES)
  );
  const userEndpointsCached = useRef<any>([]);
  userEndpointsCached.current = useMemo(
    () =>
      userDevicesLoadable.state === 'loading'
        ? userEndpointsCached.current
        : userDevicesLoadable.contents?.response?.data?.map((device: any) => ({
            ...device,
            endpointId: device.endpointId?.toString(),
            sipRegistered: device.sipRegistered?.toString(),
          })) ?? [],
    [userDevicesLoadable]
  );
  const [
    _myBusinessUsersCached,
    myBusinessUsersLoadable,
    refetchToggleMyBusinessUsers,
  ] = makeApiRequestCached<IBusinessUser | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.find(
        (user) => user.userid == id
      ) ?? cached.current,
    undefined,
    [id]
  );

  const businessUser = useMemo(
    () =>
      myBusinessUsersLoadable?.state === 'hasValue' &&
      myBusinessUsersLoadable.contents?.response?.data?.result?.find(
        (user) => user.userid == id
      ),
    [myBusinessUsersLoadable, id]
  );

  //#endregion
  //#region Form objects
  //====================
  const businessDevices = useMemo<IBusinessDevices[]>(
    () =>
      businessDevicesCached.current
        ?.filter((device: any) => device.userId === id)
        .map((device: any) => ({
          countrycode: device?.countrycode ?? 'US',
          sipUser: [' Web RTC', 'mobileapp'].includes(device.phoneType)
            ? ''
            : device.sipUser,
          sipPass: [' Web RTC', 'mobileapp'].includes(device.phoneType)
            ? ''
            : device.cred,
          deviceName:
            device.deviceType === 'webrtc' ? 'PowerCall' : device.deviceType,
          forwardNumber: device.DID ?? '',
          deviceId: device.sipUserId,
          deviceTextId: device.deviceid,
          phoneType:
            device.phoneType === ' Web RTC'
              ? 'PowerCall'
              : device.phoneType === 'cell phone'
              ? 'cell'
              : device.phoneType,
          ringSelected:
            ringGroupSettingsCached.current?.simultaneous.members.includes(
              device.sipUserId
            ) ?? true,
          forwardSelected:
            ringGroupSettingsCached.current?.linear.member === device.sipUserId,
          dialingSelected: device.isdefault === '1',
          endpointId: device?.endpointid ?? null,
        })),
    // cannot listen to refs since it does not register state changes
    [businessDevicesLoadable, ringGroupSettingsLoadable]
  );
  const kixieDevices = useMemo<any[]>(
    () =>
      userEndpointsCached.current
        ?.filter((device: any) => !device.DID)
        .map((device: any) => ({
          countrycode: device?.countrycode ?? 'US',
          sipUser: device.sipUser ?? null,
          sipPass: device.cred ?? null,
          deviceName:
            device.deviceType === 'webrtc' ? 'PowerCall' : device.deviceType,
          deviceId: device.sipUserId,
          deviceTextId: device.deviceid,
          phoneType:
            device?.phoneType?.replaceAll(' ', '') === ' Web RTC'
              ? 'PowerCall'
              : device.phoneType === 'cell phone'
              ? 'cell'
              : device.phoneType,
          ringSelected:
            ringGroupSettingsCached.current?.simultaneous.members.includes(
              device.sipUserId
            ) ?? true,
          forwardSelected:
            ringGroupSettingsCached.current?.linear.member === device.sipUserId,
          dialingSelected: device.isdefault === '1',
          endpointId: device?.endpointid ?? null,
          sipRegistered: device?.sipRegistered ?? null,
          endpointExists: device?.endpointExists ?? null,
        })),
    [userDevicesLoadable]
  );
  // all fields must default to not null/undefined or else react will give uncontrolled prop error
  const ringGroupSettings = useMemo<IRingGroupSettings>(
    () => ({
      forward: {
        'user-forward-ring':
          ringGroupSettingsCached.current?.linear.on === 'true',
        'user-forward-duration':
          ringGroupSettingsCached.current?.linear.duration ?? '',
        'user-press-one-ring':
          ringGroupSettingsCached.current?.linear.pressone === 'true',
        members: [ringGroupSettingsCached.current?.linear.member ?? ''], // derived data
      },
      simultaneous: {
        'user-simultaneous-ring':
          ringGroupSettingsCached.current?.simultaneous.on === 'true',
        'user-simultaneous-duration':
          ringGroupSettingsCached.current?.simultaneous.duration ?? '',
        members: ringGroupSettingsCached.current?.simultaneous.members ?? [], // derived data
      },
      greeting: {
        type: ringGroupSettingsCached.current?.Details?.DIDGreeting
          ? isUrl(ringGroupSettingsCached.current?.Details?.DIDGreeting)
            ? 'mp3'
            : 'tts'
          : 'none',
        value: ringGroupSettingsCached.current?.Details?.DIDGreeting ?? '',
      },
      userid: id,
    }),
    // cannot listen to refs since it does not register state changes
    [id, ringGroupSettingsLoadable]
  );
  // END DATA FETCHING CODE COPIED FROM ../../components/modals/DevicesAndRoutingModal

  const getVoicemailMessageTypeString = () => {
    const type = isUrl(businessUser?.greeting) ? 'mp3' : 'tts' ?? '';
    switch (type) {
      case 'mp3':
        return 'MP3';
      case 'tts':
        return 'Text-to-Speech';
      default:
        return NOT_SET_LABEL;
    }
  };

  const getVoicemailGreetingString = () => {
    return businessUser?.greeting ?? 'Please leave a message at the tone.';
  };

  const agentSettings = {
    voicemailMessage: {
      type: getVoicemailMessageTypeString(),
      message: getVoicemailGreetingString(),
    },
  };

  const classes = useStylesBase();
  const classesCustom = useStyles();

  const renderEditModal = (tabIndex) => () => {
    return (
      <DevicesAndRoutingModal
        id={id}
        show
        onHide={() => {
          refetchToggleMyBusinessUsers({});
          history.push(getEditUrl(EditModalHashes));
        }}
        initialTabIndex={tabIndex}
      />
    );
  };

  const getEditUrl = (hash) => `${match.url}#${hash}`;

  const getGreetingTypeString = (greetingType) => {
    switch (greetingType) {
      case 'none':
        return 'None';
      case 'tts':
        return 'Text-to-Speech';
      case 'mp3':
        return 'MP3';
      case 'record':
        return 'Record Now';
      default:
        return capitalize(greetingType);
    }
  };

  const getDeviceTypeString = (deviceType) => {
    switch (deviceType) {
      case 'cell':
        return 'Cell Phone';
      case 'desk':
        return 'Desk Phone';
      case 'other':
        return 'Other Phone';
      default:
        return capitalize(deviceType);
    }
  };

  const formatPhoneNumber = (phoneNumber) => `+${phoneNumber.replace('+', '')}`;

  const outboundDialingDevice = businessDevices.find(
    (device: IBusinessDevices) => device.dialingSelected
  );

  const mapToRenderableDevice = (device) => ({
    ...device,
    countryName: countryList.find(
      (country: ICountryList) => country.code === device.countrycode
    )?.name,
    deviceType: getDeviceTypeString(device.phoneType),
  });

  const forwardDevices = businessDevices
    ?.filter((device: IBusinessDevices) => device.forwardNumber)
    ?.map(mapToRenderableDevice);

  const devicesAndRouting = {
    inboundCallFlow: {
      audioOptions: {
        greetingType: getGreetingTypeString(ringGroupSettings.greeting.type),
        greetingMessage: ringGroupSettings.greeting.value,
        outboundDialingDevice: outboundDialingDevice
          ? getDeviceLabel(
              outboundDialingDevice.deviceName,
              outboundDialingDevice.forwardNumber
            )
          : null,
      },
      devicesToRing: {
        enableRing: ringGroupSettings.simultaneous['user-simultaneous-ring'],
        secondsToRingDevice:
          ringGroupSettings.simultaneous['user-simultaneous-duration'],
        devices: forwardDevices
          ?.concat(kixieDevices)
          ?.filter((device) => device.ringSelected)
          ?.map((device) => {
            if (device.forwardNumber) {
              return getDeviceLabel(
                device.deviceName,
                formatPhoneNumber(device.forwardNumber)
              );
            }
            return device.deviceName;
          })
          .join(', '),
      },
      callForwarding: {
        enableForwarding: ringGroupSettings.forward['user-forward-ring'],
        secondsToRingDevice: ringGroupSettings.forward['user-forward-duration'],
        forwardTo: ringGroupSettings.forward.members
          ?.map((deviceId) => {
            const device = businessDevices.find(
              (device) => device.deviceId === deviceId
            );
            return device
              ? `${device.deviceName} (${formatPhoneNumber(
                  device.forwardNumber
                )})`
              : null;
          })
          .filter((value) => !!value)
          .join(`, `),
        enableAnnounceCall: ringGroupSettings.forward['user-press-one-ring'],
      },
    },
    forwardDevices: forwardDevices?.map((device) => ({
      ...device,
      forwardNumber: formatPhoneNumber(device.forwardNumber),
    })),
    kixieDevices,
  };

  return (
    <>
      {Object.values(EditModalHashes)?.map((hash, index) => (
        <HashRoute
          path={match.url}
          hash={`#${hash}`}
          render={renderEditModal(index)}
          key={hash}
        />
      ))}
      <Paper className={`${classes.paper} ${classes.topSection}`} elevation={0}>
        <Grid container spacing={2} className={classes.topContainer}>
          <Grid item xs={10}>
            <h1>Inbound Call Flow</h1>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.InboundCallFlow)}
              className={classes.editAllLink}
            >
              <Edit />
              <br />
              <span>Edit All</span>
            </Link>
          </Grid>
        </Grid>
        <hr className={classes.topDivider} />
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2 className={classes.sectionTitle}>Routing</h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.InboundCallFlow)}
              className={classes.editLink}
            >
              <Edit />
            </Link>
          </Grid>
          <Grid item xs={12}>
            <h3 className={classes.subsectionTitle}>Initial Greeting</h3>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Greeting Type"
              value={
                devicesAndRouting.inboundCallFlow.audioOptions.greetingType
              }
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          {devicesAndRouting.inboundCallFlow.audioOptions.greetingMessage && (
            <Grid item xs={12} sm={6} md={3}>
              <InputLabel variant="standard" shrink>
                Greeting Message
              </InputLabel>
              {ringGroupSettings.greeting.type === 'tts' ? (
                <span>
                  {
                    devicesAndRouting.inboundCallFlow.audioOptions
                      .greetingMessage
                  }
                </span>
              ) : ringGroupSettings.greeting.type === 'mp3' ? (
                <audio controls className={classes.audioPlayer}>
                  <source
                    src={
                      devicesAndRouting.inboundCallFlow.audioOptions
                        .greetingMessage
                    }
                    type="audio/mpeg"
                  />
                </audio>
              ) : null}
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Dialing Device *"
              value={
                devicesAndRouting.inboundCallFlow.audioOptions
                  .outboundDialingDevice || NOT_SET_LABEL
              }
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <hr className={classes.subsectionDivider} />
          <Grid item xs={10}>
            <h3 className={classes.subsectionTitle}>Device(s) to Ring</h3>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            className={classesCustom.minHeightGridItem}
          >
            <TextField
              label="Ring Device(s)"
              value={formatOnOffString(
                devicesAndRouting.inboundCallFlow.devicesToRing.enableRing
              )}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          {devicesAndRouting.inboundCallFlow.devicesToRing.enableRing && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Seconds to Ring Device(s) *"
                  value={
                    devicesAndRouting.inboundCallFlow.devicesToRing
                      .secondsToRingDevice
                  }
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
                <br />
                <span className={classesCustom.secondsToRingDevicesInfo}>
                  (5 seconds = 1 ring)
                </span>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  label="Device(s)"
                  value={
                    devicesAndRouting.inboundCallFlow.devicesToRing.devices
                  }
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
            </>
          )}
          <hr className={classes.subsectionDivider} />
          <Grid item xs={10}>
            <h3 className={classes.subsectionTitle}>Call Forwarding</h3>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            className={classesCustom.minHeightGridItem}
          >
            <TextField
              label="Forward Call"
              value={formatOnOffString(
                devicesAndRouting.inboundCallFlow.callForwarding
                  .enableForwarding
              )}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          {devicesAndRouting.inboundCallFlow.callForwarding
            .enableForwarding && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Seconds to Ring Device(s) *"
                  value={
                    devicesAndRouting.inboundCallFlow.callForwarding
                      .secondsToRingDevice
                  }
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
                <br />
                <span className={classesCustom.secondsToRingDevicesInfo}>
                  (5 seconds = 1 ring)
                </span>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  label="Forward to *"
                  value={
                    devicesAndRouting.inboundCallFlow.callForwarding
                      .forwardTo || NOT_SET_LABEL
                  }
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <TextField
              label="Enable Announce Call"
              value={formatOnOffString(
                devicesAndRouting.inboundCallFlow.callForwarding
                  .enableAnnounceCall
              )}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <hr className={classes.subsectionDivider} />
          <Grid item xs={12}>
            <h3 className={classes.subsectionTitle}>Voicemail Message</h3>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Voicemail Type"
              value={agentSettings.voicemailMessage.type}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={9}>
            <InputLabel shrink>Voicemail Message</InputLabel>
            {isUrl(businessUser?.greeting) ? (
              <audio controls className={classes.audioPlayer}>
                <source src={businessUser?.greeting ?? ''} type="audio/mpeg" />
              </audio>
            ) : (
              <span>
                {businessUser?.greeting ??
                  'Please leave a message at the tone.'}
              </span>
            )}
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h4 className={classes.sectionTitle}>Forward Devices</h4>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.ForwardDevices)}
              className={classes.editLink}
            >
              <Edit />
            </Link>
          </Grid>
          {devicesAndRouting.forwardDevices.length === 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <span>{NOT_SET_LABEL}</span>
            </Grid>
          )}
          {devicesAndRouting.forwardDevices?.map((forwardDevice) => (
            <Fragment
              key={`${forwardDevice.countryName} ${forwardDevice.forwardNumber} ${forwardDevice.deviceName} ${forwardDevice.deviceType}`}
            >
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Country"
                  value={forwardDevice.countryName}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Forward To *"
                  value={forwardDevice.forwardNumber}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Device Name *"
                  value={forwardDevice.deviceName}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Dialing Device"
                  value={forwardDevice.deviceType}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h4 className={classes.sectionTitle}>Kixie Devices</h4>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.KixieDevices)}
              className={classes.editLink}
            >
              <Edit />
            </Link>
          </Grid>
          {devicesAndRouting.kixieDevices.length === 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <span>{NOT_SET_LABEL}</span>
            </Grid>
          )}
          {devicesAndRouting.kixieDevices?.map((kixieDevice, index) => (
            <Fragment
              key={`${kixieDevice.deviceName} ${kixieDevice.phoneType}`}
            >
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Device Name *"
                  value={kixieDevice.deviceName}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Dialing Device"
                  value={kixieDevice.phoneType}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>

              {role === Role.SUPERADMIN && (
                <>
                  <Grid className="superStyle" item xs={12} sm={6} md={3}>
                    <TextField
                      className={
                        kixieDevice.endpointExists
                          ? classesCustom.endpointExists
                          : classesCustom.endpointDoesNotExists
                      }
                      label="SIP Endpoint ID"
                      value={kixieDevice.endpointId}
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      fullWidth
                    />
                    <br />
                    <TextField
                      value={'sipRegistered = ' + kixieDevice.sipRegistered}
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      fullWidth
                    />
                  </Grid>
                  <Grid className="superStyle" item xs={12} sm={6} md={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={
                        kixieDevice.endpointExists || mutation.isLoading
                      }
                      onClick={async () => {
                        const response = await mutation.mutateAsync({
                          domain: process.env.VITE_APP_API_DOMAIN,
                          endpoint: endpointEnum.UPDATE_USER_DEVICE,
                          method: 'post',
                          payload: {
                            call: 'updateUserSipEndpoint',
                            businessId: userAtom.businessid,
                            userId: id,
                            userid: id,
                            phoneType: kixieDevice.phoneType,
                            sipUser: kixieDevice.sipUser,
                            cred: kixieDevice.sipPass,
                          },
                        });
                        setRefetchToggleForEndpoints({});
                        if (response.data.success === true) {
                          enqueueSnackbarWithStyles({
                            message: `New endpoint ${response?.data?.result?.endpointId} added for user ${id}`,
                            alertProps: {
                              severity: 'success',
                            },
                          });
                        } else {
                          enqueueSnackbarWithStyles({
                            message: `New endpoint failed to add for user ${id}`,
                            alertProps: {
                              severity: 'error',
                            },
                          });
                        }
                      }}
                    >
                      Add Endpoint
                    </Button>
                  </Grid>
                </>
              )}
              {index < devicesAndRouting.kixieDevices.length - 1 && (
                <hr className={classes.subsectionDivider} />
              )}
            </Fragment>
          ))}
        </Grid>
      </Paper>
    </>
  );
}
